import { IJSONSchema } from "../../../../base/common/jsonSchema";
import { safeStringify } from "../../../../base/common/objects";
import { IObjectTargetService } from "../../../../editor/services/project/common/documentTarget";
import { IHierarchyService } from "../../../hierarchy/common/hierarchy";
import { IInspectionService, type InspectionTargetRef } from "../../../inspection/common/inspection";
import { IInstantiationService } from "../../../instantiation/common/instantiation";
import { ILogService } from "../../../log/common/log";
import { IPropertyService, PropertyScope, PropertyViewMode } from "../../../properties/common/properties";
import { IResourceService } from "../../../resources/common/resources";
import { collectCodeOptions } from "../../../code/common/codeOptions";
import type { CodeOption } from "../../../code/common/codeView";
import { AIToolCallResponse, IAITool } from "../ai";
import { parseAiTarget, resolveAiTargetOrResource, type TargetResolverLike } from "../target";

type ReadCodeParams = {
    /** a document target from a search — `{ref?, address?}` (JSON string tolerated) */
    target?: unknown;
    resourceId?: number;
    source?: string;
    maxLength?: number;
    pattern?: string;
    context?: number;
    offset?: number;
    limit?: number;
};

/** Default/ceiling for returned code, in characters (compiled WGSL can be huge). */
const DEFAULT_MAX_LENGTH = 30_000;
const MAX_MAX_LENGTH = 120_000;

/**
 * Reads the code behind an object — the same sources the Code panel offers:
 * class source of the object/its components, authored shader strings, the
 * compiled GLSL/WGSL a material's shader actually runs as, and reconstructed
 * TSL for node materials. Target resolution mirrors `nodegraph_read`
 * (hierarchy node id → resource registry id → current selection); the option
 * menu comes from {@link collectCodeOptions}, so the AI sees exactly what a
 * user sees in the panel's source dropdown. Read-only.
 */
export class ReadCodeTool implements IAITool<ReadCodeParams> {
    readonly name = "code_read";
    // Short on purpose — see resourceTools.ts and plans/ai-tool-definition-size.md.
    readonly description =
        "Read code: a class source, authored shaders, the COMPILED GLSL/WGSL, or reconstructed TSL for node materials. " +
        "Pass a `target` from a search, a `resourceId` from resources_list, or NO ARGS for whatever is being inspected. " +
        "Call without `source` first to list what is available, then pass `source` (id or kind, e.g. 'tsl-code', " +
        "'compiled-fragment-shader') to read one. For large files use `pattern` (regex, returns matching lines with " +
        "context) or `offset`+`limit`; the response reports totalLines for paging.";
    readonly type = "function";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            target: {
                type: "object",
                description: "A TARGET returned by hierarchy_search_nodes, resources_list, property_find or property_read, passed back verbatim ({ref?, address?}) — lists/reads the code sources of that object (its class, materials, components). Never invent one: search for it.",
            },
            resourceId: {
                type: "number",
                description: "Resource registry entry id of a material (see resources_list) — lists/reads that material's code sources.",
            },
            source: {
                type: "string",
                description: "Which source to read: an option `id` or `kind` from a previous listing call. Omit to list the available sources.",
            },
            maxLength: {
                type: "number",
                description: `Character budget for the returned code (default ${DEFAULT_MAX_LENGTH}); longer output is truncated with a note.`,
                default: DEFAULT_MAX_LENGTH,
            },
            pattern: {
                type: "string",
                description: "Regex (case-insensitive) to grep the code for — returns only matching lines with context and line numbers, instead of the whole file. Best way to navigate a large compiled shader.",
            },
            context: {
                type: "number",
                description: "Lines of context to include around each `pattern` match (default 2, max 20). Ignored without `pattern`.",
            },
            offset: {
                type: "number",
                description: "1-based line number to start reading from (with `limit`, reads a line range). Ignored when `pattern` is set.",
            },
            limit: {
                type: "number",
                description: "Number of lines to read from `offset` (default 200, max 2000). Ignored when `pattern` is set.",
            },
        },
        required: [],
    };

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IResourceService private readonly resources: IResourceService,
        @IPropertyService private readonly properties: IPropertyService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
        @ILogService private readonly logger: ILogService,
    ) { }

    /**
     * The inspection service, resolved LAZILY at call time — NOT constructor-
     * injected: the AI tools are createInstance'd during product setup BEFORE
     * `IInspectionService` is set into the collection, so an injected param
     * would permanently hold nothing (found via the chat debug report: no-args
     * code_read said "Nothing is being inspected" while nodegraph_read — which
     * resolves lazily — saw the targets). Same pattern as ReadNodeGraphTool.
     */
    private get _inspection(): IInspectionService | null {
        try {
            return this.instantiation.invokeFunction((accessor) => accessor.get(IInspectionService));
        } catch {
            return null;
        }
    }

    /** the ONE resolver, lazily — absent in products without addressing */
    private get targetResolver(): TargetResolverLike | undefined {
        try {
            return this.instantiation.invokeFunction((accessor) => accessor.get(IObjectTargetService)) as TargetResolverLike;
        } catch {
            return undefined;
        }
    }

    async call(parameters?: ReadCodeParams): Promise<AIToolCallResponse> {
        // #region target resolution (same order as nodegraph_read)
        let refs: InspectionTargetRef[];
        let source: string;
        let note: string | undefined;

        // the session nodeId grammar is RETIRED (1h): a replayed stale id gets a
        // loud miss + the way forward, never a silent reinterpretation
        if ((parameters as { nodeId?: unknown } | undefined)?.nodeId !== undefined) {
            return { result: { message: "nodeId is retired — pass a `target` from hierarchy_search_nodes (or resourceId from resources_list).", success: false } };
        }

        if (parameters?.target !== undefined) {
            const parsed = parseAiTarget(parameters.target);
            if (!parsed) {
                return { result: { message: "Empty target. Omit `target` to read the current inspection, or pass a target from hierarchy_search_nodes or resources_list verbatim.", success: false } };
            }
            // element rung: a target from resources_list may name a material that
            // is no hierarchy node — matched across the weak registry by stamped id
            const object = resolveAiTargetOrResource(this.targetResolver, this.hierarchy, parsed, this.resources);
            if (!object) {
                return { result: { message: "The target could not be resolved — call hierarchy_search_nodes (or resources_list) for a current target.", success: false } };
            }
            refs = [{ target: object }];
            source = `target (${(object as { name?: string }).name || "unnamed"})`;
        }
        else if (parameters?.resourceId !== undefined) {
            if (!this.resources) {
                return { result: { message: "Resource registry not available.", success: false } };
            }
            let live: object | undefined;
            let found = false;
            for (const entry of this.resources.entries()) {
                if (entry.id !== parameters.resourceId) continue;
                found = true;
                // deref transiently — never stored (weak-registry constraint)
                live = entry.deref();
                break;
            }
            if (!found) {
                return { result: { message: `No resource registry entry with id ${parameters.resourceId}.`, success: false } };
            }
            if (!live) {
                return { result: { message: `Resource ${parameters.resourceId} was already garbage collected.`, success: false } };
            }
            refs = [{ target: live }];
            source = `resource ${parameters.resourceId}`;
        }
        else {
            // Default: whatever the USER IS CURRENTLY INSPECTING — the inspection
            // service's effective target layers (selection, a Resources click, a
            // node-graph drill-down's materials, a clicked function node). This
            // is what "read the code the user is looking at" means.
            const inspection = this._inspection;
            const targets = inspection?.getTargets() ?? [];
            if (!inspection || !targets.length) {
                return {
                    result: {
                        message: "Nothing is being inspected — select an object, or pass a `target` or resourceId.",
                        success: false,
                    },
                };
            }
            // Overrides are curated sets (drill-down materials, an Fn body) →
            // read all of them; a raw multi-select base → first only (walking
            // every owner would fire N property walks), like the code panel.
            refs = inspection.hasOverride ? [...targets] : targets.slice(0, 1);
            source = inspection.hasOverride
                ? `current inspection (${refs.map(r => r.label || "target").join(", ")})`
                : "current inspection (selection)";
            if (!inspection.hasOverride && targets.length > 1) {
                note = `${targets.length} targets selected — reading the first; pass a \`target\` to read another.`;
            }
        }
        // #endregion

        // Options per target: explicit payload options (e.g. a TSL Fn body the
        // property system can't describe) win; everything else gets the same
        // inspector-scope walk the Code panel triggers (concurrent walks are
        // deduped per object by the property service). Multiple targets merge
        // with per-target id prefixes + section labels, like the panel.
        const perRef = await Promise.all(refs.map(async (ref) => {
            const explicit = ref.payloads?.code;
            if (Array.isArray(explicit) && explicit.length) return explicit as CodeOption[];
            const col = await this.properties.getProperties(ref.target, {
                scope: PropertyScope.INSPECTOR,
                viewMode: PropertyViewMode.ALL,
            });
            return col ? collectCodeOptions(col.groups) : [];
        }));
        const multi = refs.length > 1;
        const options: CodeOption[] = [];
        perRef.forEach((refOptions, i) => {
            for (const opt of refOptions) {
                options.push(multi
                    ? { ...opt, id: `g${i}:${opt.id}`, group: refs[i].label ?? opt.group }
                    : opt);
            }
        });

        if (!options.length) {
            return {
                result: {
                    message: `No code sources on ${source}.`,
                    aiMessage: safeStringify({ source, sources: [], note, message: "This target offers no readable code." }),
                    success: true,
                },
            };
        }

        // No `source` → list the menu (cheap; code is produced only on demand).
        if (!parameters?.source) {
            return {
                result: {
                    message: `${options.length} code source${options.length === 1 ? "" : "s"} on ${source}.`,
                    aiMessage: safeStringify({
                        source,
                        note,
                        sources: options.map(o => ({ id: o.id, kind: o.kind, label: o.label, section: o.group })),
                        hint: "Call again with `source` set to an id (exact) or kind to read that code.",
                    }),
                    success: true,
                },
            };
        }

        const wanted = parameters.source;
        const option = this._match(options, wanted);
        if (!option) {
            return {
                result: {
                    message: `No code source '${wanted}' on ${source}. ` +
                        `Available: ${options.map(o => `${o.id} (${o.kind})`).join(", ")}.`,
                    success: false,
                },
            };
        }

        let code: string | null = null;
        try {
            code = await option.produce();
        } catch (err) {
            this.logger.warn(`[code_read] producing '${option.id}' failed: ${err instanceof Error ? err.message : err}`);
        }
        if (typeof code !== "string" || !code.length) {
            return {
                result: {
                    message: `Code source '${option.id}' produced nothing — the source isn't readable right now ` +
                        "(e.g. a compiled shader before the material was ever rendered).",
                    success: false,
                },
            };
        }

        const budget = Math.min(Math.max(1000, parameters.maxLength ?? DEFAULT_MAX_LENGTH), MAX_MAX_LENGTH);
        // Shape the produced source: grep (pattern), a line range (offset/limit),
        // or the whole file — always reporting totalLines so the AI can page. All
        // three then obey the char budget. Operates on whatever produce() returned
        // (already tier-gated for compiled/TSL), so it never widens access.
        const shaped = shapeCode(code, parameters);
        if ("empty" in shaped) {
            return {
                result: {
                    message: `No lines match /${parameters.pattern}/i in '${option.label}'.`,
                    aiMessage: safeStringify({
                        source, id: option.id, kind: option.kind, label: option.label,
                        section: option.group, note, totalLines: shaped.totalLines,
                        pattern: parameters.pattern, matches: 0,
                    }),
                    success: true,
                },
            };
        }
        if ("error" in shaped) {
            return { result: { message: shaped.error, success: false } };
        }

        const assembledLength = shaped.code.length;
        const overBudget = assembledLength > budget;
        const outCode = overBudget ? shaped.code.slice(0, budget) : shaped.code;

        return {
            result: {
                message: `Read '${option.label}' (${option.kind}) from ${source}.`,
                aiMessage: safeStringify({
                    source,
                    id: option.id,
                    kind: option.kind,
                    label: option.label,
                    section: option.group,
                    language: option.language ?? "javascript",
                    note,
                    totalLines: shaped.totalLines,
                    ...shaped.meta,
                    truncated: overBudget
                        ? `output truncated to ${budget} of ${assembledLength} characters — narrow with pattern/offset/limit or raise maxLength`
                        : undefined,
                    code: outCode,
                }),
                success: true,
            },
        };
    }

    /** Exact id first, then kind, then label — case-insensitive for the latter two. */
    private _match(options: CodeOption[], wanted: string): CodeOption | undefined {
        const lower = wanted.toLowerCase();
        return options.find(o => o.id === wanted)
            ?? options.find(o => o.kind.toLowerCase() === lower)
            ?? options.find(o => o.label.toLowerCase() === lower);
    }
}

/**
 * Turns produced source into the requested view: a `pattern` grep (matching
 * lines + `context`, merged into ripgrep-style blocks with line numbers), an
 * `offset`/`limit` line range, or the whole file. Line-numbered output lets the
 * AI grep, then page to a hit. Returns `{empty}` when a pattern matched nothing,
 * `{error}` on a bad regex. Pure — exported so the shaping is unit-testable.
 */
export function shapeCode(code: string, p: Pick<ReadCodeParams, "pattern" | "context" | "offset" | "limit">):
    | { code: string; totalLines: number; meta: Record<string, unknown> }
    | { empty: true; totalLines: number }
    | { error: string } {
    const lines = code.split("\n");
    const totalLines = lines.length;

    if (p.pattern) {
        let re: RegExp;
        try {
            re = new RegExp(p.pattern, "i");
        } catch (err) {
            return { error: `Invalid pattern regex: ${err instanceof Error ? err.message : String(err)}` };
        }
        const ctx = Math.min(Math.max(0, p.context ?? 2), 20);
        const hits: number[] = [];
        for (let i = 0; i < lines.length; i++) if (re.test(lines[i])) hits.push(i);
        if (!hits.length) return { empty: true, totalLines };
        // Union of each hit's context window, then emit contiguous runs as
        // blocks separated by `--` (so the AI sees where gaps are).
        const keep = new Set<number>();
        for (const i of hits) for (let j = i - ctx; j <= i + ctx; j++) if (j >= 0 && j < lines.length) keep.add(j);
        const sorted = [...keep].sort((a, b) => a - b);
        const parts: string[] = [];
        let prev = -2;
        for (const i of sorted) {
            if (parts.length && i !== prev + 1) parts.push("--");
            parts.push(`${i + 1}\t${lines[i]}`);
            prev = i;
        }
        return { code: parts.join("\n"), totalLines, meta: { pattern: p.pattern, matches: hits.length } };
    }

    if (p.offset !== undefined || p.limit !== undefined) {
        const start = Math.max(1, Math.floor(p.offset ?? 1)); // 1-based line
        const count = Math.min(Math.max(1, Math.floor(p.limit ?? 200)), 2000);
        const end = Math.min(start - 1 + count, totalLines);
        const slice = lines.slice(start - 1, end);
        const numbered = slice.map((l, k) => `${start + k}\t${l}`).join("\n");
        return { code: numbered, totalLines, meta: { lines: `${start}-${end} of ${totalLines}` } };
    }

    return { code, totalLines, meta: {} };
}
